'use client'

import Link from 'next/link'
import { ArrowRight, FileText } from 'lucide-react'
import { useRegion } from '@/contexts/marketing-region-context'
import { PricingRegion } from '@/lib/billing/region'

type HeroNums = {
  jobA: number   // approved job value
  jobB: number   // quoted job value
  jobC: number   // in-progress job value
  outstanding: number
  revenue: number
}

const HERO_NUMS: Record<PricingRegion, HeroNums> = {
  ZA: { jobA: 12_420, jobB: 8_600,  jobC: 15_200, outstanding: 12_420, revenue: 84_500 },
  UK: { jobA: 550,    jobB: 385,    jobC: 675,    outstanding: 550,    revenue: 3_750  },
  EU: { jobA: 620,    jobB: 435,    jobC: 760,    outstanding: 620,    revenue: 4_200  },
  AU: { jobA: 1_000,  jobB: 700,    jobC: 1_240,  outstanding: 1_000,  revenue: 6_800  },
  NZ: { jobA: 1_150,  jobB: 810,    jobC: 1_420,  outstanding: 1_150,  revenue: 7_850  },
}

function trackCta(cta: string) {
  try {
    fetch('/api/analytics/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_name: 'marketing_cta_clicked', properties: { location: 'hero', cta } }),
    }).catch(() => {})
  } catch { /* ignore */ }
}

function MobileHeroPreview({ value }: { value: string }) {
  return (
    <div className="mt-7 lg:hidden">
      <div className="rounded-[1.35rem] border border-white/12 bg-white/[0.06] p-3.5 shadow-[0_24px_70px_rgba(0,0,0,0.35)]">
        <div className="rounded-2xl bg-[#101925] p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] font-semibold uppercase text-white/58">Site visit saved</p>
            <span className="rounded-full bg-emerald-500/14 px-2.5 py-1 text-[10px] font-bold text-emerald-300">Quote ready</span>
          </div>

          <div className="mt-3 rounded-xl border border-white/8 bg-white/[0.04] p-3">
            <p className="text-sm font-semibold leading-snug text-white">Bathroom repaint and crack repair</p>
            <p className="mt-1.5 text-[12.5px] leading-relaxed text-white/68">Photos, measurements, notes, labour, materials, deposit, and approval are kept on the job.</p>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2.5">
            <div className="rounded-xl bg-[#192535] p-3">
              <p className="text-[10px] font-medium text-white/48">Quote value</p>
              <p className="mt-1 text-lg font-black leading-none text-[#c7681f]">{value}</p>
            </div>
            <div className="rounded-xl bg-[#192535] p-3">
              <p className="text-[10px] font-medium text-white/48">Client link</p>
              <p className="mt-1 text-sm font-bold text-white">Approve online</p>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between rounded-xl bg-[#c2571a] px-3.5 py-3 text-white">
            <span className="text-sm font-bold">Send from your phone</span>
            <ArrowRight size={16} strokeWidth={2} aria-hidden />
          </div>
        </div>
      </div>
    </div>
  )
}

export function MarketingHero() {
  const { region, fmt } = useRegion()
  const n = HERO_NUMS[region]

  return (
    <section className="relative flex min-h-[calc(100svh-1rem)] items-start overflow-hidden bg-[#0a1422] lg:min-h-[92vh] lg:items-center">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1422] via-[#152237]/60 to-[#0a1422]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_75%_65%_at_50%_0%,rgba(25,52,85,0.50),transparent)]" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute -top-40 -right-40 hidden h-[700px] w-[700px] rounded-full bg-[#c2571a]/5 blur-[120px] lg:block" />
        <div className="absolute bottom-0 left-1/4 hidden h-[400px] w-[400px] rounded-full bg-[#1e3a5f]/20 blur-[100px] lg:block" />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-4 pb-12 pt-20 sm:px-6 sm:pt-28 lg:px-8 lg:pb-16">
        <div className="grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">

          {/* ── Left: Copy ─────────────────────────────────────────── */}
          <div>
            {/* Brand eyebrow. Follows the site-wide eyebrow convention
                (uppercase, tracked, brand orange) rather than the pill it used
                to be — #f3c29e is a lightened tint of the brand orange, as
                #c2571a is too dark to read against this background. */}
            <div className="mb-5 flex items-center gap-3 sm:mb-7">
              <span className="h-px w-7 bg-gradient-to-r from-[#c2571a]/0 to-[#c2571a]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#f3c29e]">
                The better way to build
              </span>
            </div>

            {/* Headline */}
            <h1 className="max-w-[780px] text-[2.6rem] font-extrabold leading-[1.02] text-white sm:text-[3.7rem] lg:text-[4.15rem]">
              Build more than the next job.
            </h1>

            {/* Sub */}
            <p className="mt-4 max-w-[560px] text-[1.02rem] leading-[1.58] text-white/78 sm:mt-5 sm:text-[1.12rem] sm:leading-[1.68] sm:text-white/64">
              <span className="sm:hidden">Quotes, invoices, and job admin from your phone — so the admin takes minutes, not your evenings.</span>
              <span className="hidden sm:inline">
                BuilderBuddi runs your quotes, invoices, and job admin from your phone — so the admin takes minutes instead of your evenings.
              </span>
            </p>

            {/* CTAs */}
            <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:flex-wrap">
              <Link
                href="/signup"
                className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#c2571a] px-7 py-4 text-[15px] font-semibold text-white shadow-[0_8px_28px_rgba(194,87,26,0.45)] transition-all hover:-translate-y-0.5 hover:bg-[#a84915] active:translate-y-0 sm:w-auto"
                onClick={() => trackCta('start_free')}
              >
                Start Free
                <ArrowRight size={16} strokeWidth={2} aria-hidden />
              </Link>
              <a
                href="#workflow"
                className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-7 py-3.5 text-[15px] font-semibold text-white/82 transition-all hover:bg-white/10 hover:text-white sm:min-h-14 sm:w-auto sm:py-4"
                onClick={() => trackCta('see_how_it_works')}
              >
                <FileText size={15} strokeWidth={2} aria-hidden />
                See How It Works
              </a>
            </div>

            {/* Trust signals */}
            <div className="mt-5 grid grid-cols-1 gap-2 sm:mt-7 sm:flex sm:flex-wrap sm:gap-x-5 sm:gap-y-2">
              {[
                'No credit card required',
                'Start with one job',
                'Works on your phone',
              ].map((item) => (
                <span key={item} className="flex items-center gap-1.5 text-[13px] font-medium text-white/72 sm:text-white/40">
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <path d="M2 6.5l3 3 6-6" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {item}
                </span>
              ))}
            </div>

            <MobileHeroPreview value={fmt(n.jobA)} />

            {/* Workflow strip */}
            <div className="mt-9 hidden sm:flex items-center gap-1.5 flex-wrap">
              {['Site Visit', 'Notes', 'Quote', 'Approve', 'Track', 'Invoice', 'Paid'].map((step, i) => (
                <span key={step} className="flex items-center gap-1.5">
                  <span className="text-[11px] font-semibold uppercase text-white/35">{step}</span>
                  {i < 6 && (
                    <ArrowRight size={9} strokeWidth={2} className="text-[#c7681f]" aria-hidden />
                  )}
                </span>
              ))}
            </div>
          </div>

          {/* ── Right: App UI Mockup ────────────────────────────────── */}
          <div className="mx-auto hidden w-full max-w-[500px] lg:block lg:max-w-none">
            {/* Browser frame */}
            <div className="rounded-2xl overflow-hidden border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.60),0_8px_32px_rgba(0,0,0,0.40)]">
              {/* Browser chrome */}
              <div className="bg-[#0f1822] px-4 py-2.5 flex items-center gap-3 border-b border-white/6">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/50" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/50" />
                </div>
              </div>

              {/* App shell */}
              <div className="bg-[#131924]">
                {/* App topbar */}
                <div className="bg-[#131924] border-b border-white/7 px-4 py-3 flex items-center gap-2.5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-xl shrink-0" style={{ background: 'linear-gradient(135deg, #c55918 0%, #a84515 100%)' }}>
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="white">
                      <path d="M8 1L14 5V11L8 15L2 11V5L8 1Z" fill="white" fillOpacity="0.9"/>
                    </svg>
                  </div>
                  <span className="text-[12px] font-bold text-white flex-1">BuilderBuddi</span>
                  <div className="flex items-center gap-1.5">
                    <div className="w-6 h-6 rounded-full bg-[#c2571a] flex items-center justify-center shrink-0">
                      <span className="text-[9px] text-white font-bold">J</span>
                    </div>
                  </div>
                </div>

                {/* Dashboard content */}
                <div className="p-3.5 space-y-3">
                  {/* Greeting */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] text-white/40 font-medium">Good morning</p>
                      <p className="text-[14px] font-bold text-white leading-tight">Today&apos;s overview</p>
                    </div>
                    <span className="text-[10px] text-white/30 font-medium bg-white/5 rounded-lg px-2.5 py-1.5 border border-white/7">
                      Dashboard
                    </span>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="rounded-xl bg-[#192535] border border-white/6 px-3 py-2.5">
                      <p className="text-[9px] text-white/40 font-medium mb-0.5">Active Jobs</p>
                      <p className="text-[20px] font-extrabold text-white leading-tight">5</p>
                      <p className="text-[9px] text-emerald-400 font-semibold">+2 this week</p>
                    </div>
                    <div className="rounded-xl bg-[#192535] border border-white/6 px-3 py-2.5">
                      <p className="text-[9px] text-white/40 font-medium mb-0.5">Outstanding</p>
                      <p className="text-[14px] font-extrabold text-[#c7681f] leading-tight truncate">{fmt(n.outstanding)}</p>
                      <p className="text-[9px] text-white/35 font-medium">2 invoices</p>
                    </div>
                    <div className="rounded-xl bg-[#192535] border border-white/6 px-3 py-2.5">
                      <p className="text-[9px] text-white/40 font-medium mb-0.5">This Month</p>
                      <p className="text-[14px] font-extrabold text-emerald-400 leading-tight truncate">{fmt(n.revenue)}</p>
                      <p className="text-[9px] text-emerald-400 font-semibold">↑ 14%</p>
                    </div>
                  </div>

                  {/* Jobs list */}
                  <div>
                    <p className="text-[10px] text-white/35 font-semibold uppercase tracking-[0.12em] mb-2">Recent Jobs</p>
                    <div className="space-y-1.5">

                      {/* Job A — Approved */}
                      <div className="rounded-xl bg-[#192535] border border-white/6 px-3 py-2.5 flex items-center gap-2.5">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-400">
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l2.5 2.5 5.5-5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-bold text-white truncate">Kitchen Renovation</p>
                          <p className="text-[10px] text-white/40">J. Smith · {fmt(n.jobA)}</p>
                        </div>
                        <span className="shrink-0 text-[9px] font-bold text-emerald-400 bg-emerald-500/12 px-2 py-0.5 rounded-full border border-emerald-500/20">
                          Approved ✓
                        </span>
                      </div>

                      {/* Job B — Quote Sent */}
                      <div className="rounded-xl bg-[#192535] border border-white/6 px-3 py-2.5 flex items-center gap-2.5">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-400">
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                            <path d="M10.5 1.5H1.5a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1zM3 4h6M3 6.5h4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-bold text-white truncate">Office Retile</p>
                          <p className="text-[10px] text-white/40">M. Brown · {fmt(n.jobB)}</p>
                        </div>
                        <span className="shrink-0 text-[9px] font-bold text-amber-400 bg-amber-500/12 px-2 py-0.5 rounded-full border border-amber-500/20">
                          Quote Sent
                        </span>
                      </div>

                      {/* Job C — In Progress */}
                      <div className="rounded-xl bg-[#192535] border border-white/6 px-3 py-2.5 flex items-center gap-2.5">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-500/15 text-blue-400">
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                            <path d="M11 6A5 5 0 1 1 6 1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                            <path d="M6 3v3l2 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-bold text-white truncate">Bathroom Reno</p>
                          <p className="text-[10px] text-white/40">L. Patel · {fmt(n.jobC)}</p>
                        </div>
                        <span className="shrink-0 text-[9px] font-bold text-blue-400 bg-blue-500/12 px-2 py-0.5 rounded-full border border-blue-500/20">
                          In Progress
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Quick action bar */}
                  <div className="flex gap-2 pt-0.5">
                    <div className="flex-1 flex items-center gap-1.5 rounded-xl bg-[#c2571a] px-3 py-2 cursor-pointer">
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                        <path d="M6 1v10M1 6h10" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      <span className="text-[10px] font-bold text-white">New Quote</span>
                    </div>
                    <div className="flex-1 flex items-center gap-1.5 rounded-xl bg-white/6 border border-white/8 px-3 py-2 cursor-pointer">
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                        <rect x="1" y="1" width="4" height="4" rx="0.7" stroke="#a2b8cc" strokeWidth="1.1"/>
                        <rect x="7" y="1" width="4" height="4" rx="0.7" stroke="#a2b8cc" strokeWidth="1.1"/>
                        <rect x="1" y="7" width="4" height="4" rx="0.7" stroke="#a2b8cc" strokeWidth="1.1"/>
                        <rect x="7" y="7" width="4" height="4" rx="0.7" stroke="#a2b8cc" strokeWidth="1.1"/>
                      </svg>
                      <span className="text-[10px] font-semibold text-white/50">Estimators</span>
                    </div>
                    <div className="flex-1 flex items-center gap-1.5 rounded-xl bg-white/6 border border-white/8 px-3 py-2 cursor-pointer">
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                        <rect x="0.75" y="0.75" width="10.5" height="10.5" rx="1.5" stroke="#a2b8cc" strokeWidth="1.1"/>
                        <path d="M2.5 4h7M2.5 6.5h5" stroke="#a2b8cc" strokeWidth="1" strokeLinecap="round"/>
                      </svg>
                      <span className="text-[10px] font-semibold text-white/50">Invoices</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating stat chips */}
            <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/6 px-3.5 py-2">
                <span className="flex h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[11.5px] font-semibold text-white/55">Quote sent in 4 min</span>
              </div>
              <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/6 px-3.5 py-2">
                <span className="flex h-1.5 w-1.5 rounded-full bg-[#c7681f]" />
                <span className="text-[11.5px] font-semibold text-white/55">Client approved online</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
        <svg viewBox="0 0 1440 64" fill="none" preserveAspectRatio="none" className="w-full h-12 text-white">
          <path d="M0 64L1440 64L1440 32C1200 4 960 56 720 32C480 8 240 60 0 32L0 64Z" fill="currentColor" />
        </svg>
      </div>
    </section>
  )
}
